<template>
  <BaseWidget
    :class="['company-profile']"
    :loading="loading"
    @refresh-data="refreshData(true)" 
    @set-config="setConfig"
    v-on="$listeners"
  >
    <template v-slot:header>
      <div class="title">
        <span class="title-icon">
        </span>
        {{ title }}
      </div>
      <div class="description">{{ getFilterText() }}</div>
    </template>
    <template v-slot:content>
      <portal to="dash-modal">
        <ImportKvkModal v-if="showImportKvkModal" :target-data="targetData" @data-updated="refreshData(true)" @close-modal="showImportKvkModal = false" />
      </portal>
      <div class="company-profile-wrap">
        <template v-if="!loading && companyMissingFields.length > 0">
          <div class="incomplete-content-wrap">
            <div class="incomplete-content">
              <h3>{{ completionText }}</h3>
              <p class="mb-3">{{ $gettext('Fill the form to reach 100%') }}</p>
              <GekkoButton 
                v-if="progress < 100"
                @click="openWizard()"
                size="sm"
                :text="$gettext('Complete my profile')"
              />
            </div>
          </div>
        </template>
        <template v-if="!loading && companyMissingFields.length === 0">
          <AnimationItem class="anim-success" :animation="{ animationData: successAnimation.default }" :key="'anim_success'" />
          <div class="progress-wrap">
            <h3>🤟 {{ $gettext('Well done!') }}</h3>
            {{ $gettext('Your company profile is complete.') }}
            <div class="progress-wrap--hint" @click="gotoSettings()" v-html="$gettext('To update your data go to <span class=\'remove-widget-link\'>company settings</span> page.')" />
            <div class="progress-wrap--hint" @click="removeWidget()" v-html="$gettext('Also you can <span class=\'remove-widget-link\'>remove this widget</span>.')" />
          </div>
        </template>
      </div>
    </template>
    <template v-slot:actions>
      <div v-if="progress == 1000">
        <a :href="'/en/accounts/companies/'">{{ $gettext('Edit company profile') }}</a>
      </div>
    </template>
  </BaseWidget>
</template>

<script>
import { mapGetters, mapMutations, mapActions } from "vuex";
import BaseWidget from "@/components/dashboard/widgets/BaseWidget"
import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'
import * as successAnimation from '@/css/animations/success.json';
import ImportKvkModal from "@/components/dashboard/ui/ImportKvkModal.vue";
import GekkoButton from '@/components/GekkoButton';
import DoughnutChart from '@/components/dashboard/charts/Doughnut'

export default {
  name: 'company-profile-widget',
  components: { BaseWidget, AnimationItem, ImportKvkModal, GekkoButton, DoughnutChart },
  props: {
    widget: {
      type: Object,
      default: () => {}
    }
  },
  data() {
    return {
      loading: true,
      title: '',
      subtitle: '',
      config: { status: '' },
      filters: {},
      successAnimation: successAnimation,
      showImportKvkModal: false
    }
  },
  computed: {
    ...mapGetters({
      currentCompany: "main/getCurrentCompany",
      companyMissingFields: 'main/getCompanyMissingFields',
      targetData: 'main/getCompanyRequiredData',
    }),
    progress () {
      return Math.round(((this.targetData.length - this.companyMissingFields.length) / this.targetData.length) * 100 )
    },
    completionText () {
      let text = ''
      if (this.progress < 25) {
        text = this.$gettext('Fresh start!')
      } else if (this.progress < 50) {
        text = this.$gettext('Keep it up!')
      } else if (this.progress < 75) {
        text = this.$gettext('Nearly there!')
      } else if (this.progress < 100) {
        text = this.$gettext('Almost done!')
      }
      return text
    }
  },
  created () {
    // if (this.isCompanyDutch()) {
    //   const kvkTargetItem = { key: 'kvk', label: this.$gettext('Kvk number') }
    //   this.targetData.push(kvkTargetItem)
    // }
  },
  mounted () {
    this.setupWidget()
    this.setObjectStatus(this.widget.config)
  },
  methods: {
    ...mapActions({ 
      loadCurrentCompany: "main/loadCurrentCompany"
    }),
    ...mapMutations({ 
      openOnboardingWizard: 'onboarding/OPEN_ONBOARDING_WIZARD',
    }),
    // isCompanyDutch() {
    //   return this.currentCompany.country === "NL";
    // },
    getFilterText () {
      let text = this.$gettext('Company profile status')
      text = (this.companyMissingFields.length > 0) ? this.$gettext('Profile status: incomplete') : this.$gettext('Profile status: Complete!')
      return text
    },
    setConfig (config) {
      if (config.status) this.setObjectStatus(config)
      this.$emit('save-config', this.config)
    },
    setObjectStatus (config) {
      if (config.status) {
        this.config.status = (config.status && config.status !== 'all') ? config.status : ''
      } 
      this.setFilters()
    },
    setFilters (status = false) {
      this.filters.status = (status) ? (status === 'all') ? '' : status : this.config.status
      this.refreshData()
    },
    refreshData (forceRefresh = false) {
      if (forceRefresh) {
        this.getData()
      } else {
        this.getStoreData()
      }
    },
    updateFilters (status) {
      this.filters.status = (status === 'all') ? '' : status
      this.setFilters(status)
    },
    getData () {
      this.loading = true
      this.loadCurrentCompany().then(() => {
        this.setChartData()
        this.loading = false
      })
    },
    getStoreData () {
      this.setChartData()
      this.loading = false
    },
    setChartData () {
      this.chartData = {
        labels: [''],
        datasets: [
          {
            backgroundColor: ['#22AC8A'], // $colorBrand
            data: [this.progress, 100 - this.progress],
            borderWidth: 0,
            hoverBorderWidth: 0
          }
        ]
      }
    },
    setupWidget () {
      this.title = this.$gettext('Company profile')
    },
    removeWidget () {
      this.$emit('widget-event', { 'event': 'remove' })
      setTimeout(() => {
        this.$emit('widget-event', { 'event': 'update', 'target': 'activities-timeline-widget' })
      }, 100);
    },
    gotoSettings () {
      window.location.href = '/en/accounts/companies/'
    },
    openImportKvkModal() {
      document.body.classList.add('modal-on');
      this.showImportKvkModal = true;
    },
    openWizard() {
      this.openOnboardingWizard()
    }
  }
}
</script>

<style lang="scss">
.company-profile {
  .company-profile-wrap {
    display: flex;
    align-content: center;
    justify-content: center;
    width: 100%;
  }
  .progress-wrap {
    width: 100%;
    text-align: center;
    position: relative;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
    flex-direction: column;
    line-height: 1.2;
    &--hint {
      font-size: 14px;
      color: #0000007d;
      margin-top: 5px;
    }
  }
  .incomplete-wrap {
    width: 100%;
    position: relative;
    font-size: 14px;
    .missing-list {
      list-style: none;
      margin: 0; margin-top: 10px;
      padding: 0;
      padding-left: 15px;
      li {
        padding-left: 20px;
        position: relative;
        &:before {
          content: '';
          width: 8px;
          height: 8px;
          border-radius: $radiusXl;
          position: absolute;
          top: 50%;
          left: 5px;
          transform: translateY(-50%);
          background: $colorBrand;
        }
      }
    }
  }

  .incomplete-content-wrap {
    display: flex;
    width: 100%;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    line-height: 1;
    .progress-count {
      font-size: 20px;
      font-family: DINNextLTPro-Medium, sans-serif;
      color: $colorAppInvoices;
      margin-bottom: 0.5rem;
      position: relative;
      .progress-number {
        position: absolute;
        top: 50%;
        left: 50%;
        transform: translate(-50%,-50%);
      }
      .donut-wrap {
        width: 75px;
        height: 75px;
      }
      // canvas {
      //   width: 75px;
      //   height: 75px;
      // }
    } 
    .incomplete-content { 
      display: flex;
      flex-direction: column;
      flex: 1;
      text-align: center;
      justify-content: flex-end;
      align-items: center;
      h4 { margin: 0; margin-bottom: 0.5rem; }
      p { display: none; font-size: 14px; color: $colorUnassigned; }
      .btn {  white-space: nowrap;}
    }

    @media (min-width: '992px') {
      flex-direction: row;
      .progress-count {
        font-size: 26px;
        font-family: DINNextLTPro-Medium, sans-serif;
      }
      .incomplete-content { 
        p { display: block; }
        padding-left: 0.25rem;
      }
    }

  }


  .anim-success {
    width: 100%;
    height: 100%;
    margin: 0 auto;
    position: absolute;
    top: 0;
    left: 0;
  }
  .remove-widget-link {
    text-decoration: underline;
    cursor: pointer;
  }
}
</style>
